import React, { useMemo } from "react";
import { ChatListItem } from "@web-src/modules/chats/components/ChatListItem";
import { EmailConversationModel } from "@jugl-web/rest-api";
import {
  getEmailPreviewText,
  getUniqueId,
  useTranslations,
} from "@jugl-web/utils";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { ChatListItemLastMessage } from "@web-src/modules/chats/components/ChatListItemLastMessage";
import { ChatMessageBodyToken } from "@web-src/modules/chats/types";
import { emailMessageToAttachmentsPayload } from "@web-src/modules/client-support/utils/emailMessageToAttachmentsPayload";
import { splitEmailTrimmedContent } from "@web-src/modules/client-support/utils/splitEmailTrimmedContent";
import { unwrapMachineDump } from "@web-src/modules/client-support/utils/unwrapMachineDump";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { getEmailConversationContactName } from "../../../../utils/getEmailConversationContactName";
import { EmailAvatar } from "../../../EmailAvatar";
import { CRMAgentInfo } from "../CRMAgentInfo";

export const EmailConversationTile: React.FC<{
  conversation: EmailConversationModel;
  searchQuery?: string;
}> = ({ conversation, searchQuery }) => {
  const { t } = useTranslations();
  const { selectedConversation, selectConversation } =
    useClientSupportPageContext();
  const { entityId } = useEntitySelectedProvider();

  const displayName = useMemo(
    () => getEmailConversationContactName(conversation),
    [conversation]
  );

  const attachments = useMemo(
    () =>
      emailMessageToAttachmentsPayload({
        message: conversation,
        entityId,
      }) || undefined,
    [conversation, entityId]
  );

  const lastMessageBody: ChatMessageBodyToken[] | null = useMemo(() => {
    const { body } = splitEmailTrimmedContent(conversation.payload.text ?? "");
    const content = getEmailPreviewText(unwrapMachineDump(body));

    if (!content) return null;

    return [
      {
        id: getUniqueId(),
        type: "string",
        humanizedString: content,
      },
    ];
  }, [conversation]);

  return (
    <ChatListItem
      variant="inbox"
      chatId={conversation.conv_id}
      onClick={() => selectConversation(conversation)}
      timestamp={conversation.payload.timestamp}
      isSelected={selectedConversation?.conv_id === conversation.conv_id}
      title={displayName}
      img={
        <EmailAvatar className="h-10 w-10 border border-solid border-grey-300" />
      }
      lastMessageContent={
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-medium text-dark-800">
            {conversation.name ||
              t({
                id: "client-support-page.email-no-subject",
                defaultMessage: "(No subject)",
              })}
          </span>
          <span className="truncate text-sm text-dark-600">
            <ChatListItemLastMessage
              payloadVersion={CHAT_MESSAGE_PAYLOAD_VERSION}
              tokenizedMessage={lastMessageBody || []}
              attachments={attachments}
            />
          </span>
        </div>
      }
      additionalContent={<CRMAgentInfo conversation={conversation} />}
      showUnreadIndicator={
        conversation.type === "incoming" && !conversation.read
      }
      searchQuery={searchQuery}
      hasChatAccess
    />
  );
};
